import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import {
  getMfnTriggerWithStale,
  updateMfnTrigger,
  TRIGGER_STATUS_LABELS,
  type MfnTriggerStatus,
} from "@/lib/api/mfn";
import { supabase } from "@/lib/supabase/client";
import { listLps } from "@/lib/api/lps";
import { useRole } from "@/lib/auth/use-role";
import { useAuth } from "@/lib/auth/auth-provider";
import { CATEGORY_LABELS } from "@argus/engine";
import type { ProvisionRow } from "@/lib/api/provisions";
import { Button } from "@/components/ui/primitives";
import { fmtDateTime } from "@/lib/format";

/** The provision that fired the trigger plus the MFN clause's own provision. */
async function loadContext(triggeringProvisionId: string, mfnClauseId: string) {
  const [{ data: triggering, error: e1 }, { data: clause, error: e2 }] = await Promise.all([
    supabase
      .from("provisions")
      .select("id, document_id, raw_text, summary, category, structured_value, review_status")
      .eq("id", triggeringProvisionId)
      .single(),
    supabase
      .from("mfn_clauses")
      .select("id, provision_id, scope, threshold_commitment_usd, excluded_categories")
      .eq("id", mfnClauseId)
      .single(),
  ]);
  if (e1) throw new Error(e1.message);
  if (e2) throw new Error(e2.message);
  const { data: clauseProvision, error: e3 } = await supabase
    .from("provisions")
    .select("id, document_id, raw_text, summary, category, structured_value, review_status")
    .eq("id", (clause as { provision_id: string }).provision_id)
    .single();
  if (e3) throw new Error(e3.message);
  return {
    triggering: triggering as unknown as ProvisionRow,
    clauseProvision: clauseProvision as unknown as ProvisionRow,
  };
}

const NEXT_STATUSES: MfnTriggerStatus[] = [
  "reviewing",
  "extending",
  "negotiating",
  "resolved_extended",
  "resolved_excluded",
  "resolved_negotiated",
  "dismissed",
];

function ProvisionExcerpt({ title, provision }: { title: string; provision: ProvisionRow }) {
  return (
    <div className="rounded border border-neutral-800 bg-neutral-900/60 p-3">
      <h3 className="text-[11px] uppercase tracking-wider text-neutral-500">{title}</h3>
      <p className="mt-1 text-xs font-medium text-neutral-200">
        {CATEGORY_LABELS[provision.category]} ·{" "}
        <span className="font-normal text-neutral-400">{provision.summary}</span>
      </p>
      <blockquote className="mt-2 max-h-44 overflow-y-auto whitespace-pre-wrap border-l-2 border-neutral-700 pl-3 font-mono text-[11px] leading-relaxed text-neutral-400">
        {provision.raw_text}
      </blockquote>
    </div>
  );
}

function MfnDetailInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const params = useParams({ strict: false });
  const triggerId = params.triggerId ?? "";
  const qc = useQueryClient();
  const { canWrite, role } = useRole(tenant.id);
  const canReview = canWrite || role === "reviewer";

  const [nextStatus, setNextStatus] = useState<MfnTriggerStatus | "">("");
  const [notes, setNotes] = useState("");

  const {
    data: trigger,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["mfn-trigger", triggerId],
    queryFn: () => getMfnTriggerWithStale(triggerId),
    enabled: !!triggerId,
  });
  const { data: lps } = useQuery({
    queryKey: ["lps", tenant.id],
    queryFn: () => listLps(tenant.id),
  });
  const { data: context } = useQuery({
    queryKey: ["mfn-trigger-context", triggerId],
    queryFn: () => {
      if (!trigger) throw new Error("Trigger not loaded yet.");
      return loadContext(trigger.triggering_provision_id, trigger.mfn_clause_id);
    },
    enabled: !!trigger,
  });

  const transition = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("Not signed in.");
      if (!nextStatus) throw new Error("Pick a status.");
      return updateMfnTrigger(triggerId, user.id, {
        status: nextStatus,
        resolution_notes: notes || null,
      });
    },
    onSuccess: () => {
      setNextStatus("");
      setNotes("");
      void qc.invalidateQueries({ queryKey: ["mfn-trigger", triggerId] });
      void qc.invalidateQueries({ queryKey: ["mfn-triggers", tenant.id] });
    },
  });

  if (isLoading) return <p className="text-sm text-neutral-500">Loading trigger…</p>;
  if (error || !trigger) {
    return (
      <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
        {error?.message ?? "Trigger not found."}
      </div>
    );
  }

  const lpName = (id: string): string => lps?.find((l) => l.id === id)?.legal_name ?? "—";

  return (
    <>
      <header>
        <Link
          to="/t/$slug/mfn"
          params={{ slug: tenant.slug }}
          className="text-xs text-neutral-500 hover:text-neutral-300"
        >
          ← MFN triggers
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">
          {lpName(trigger.holder_lp_id)}&rsquo;s MFN vs {lpName(trigger.beneficiary_lp_id)}
        </h1>
        <p className="text-sm text-neutral-400">
          Category <span className="text-neutral-200">{CATEGORY_LABELS[trigger.category]}</span> ·
          status <span className="text-neutral-200">{TRIGGER_STATUS_LABELS[trigger.status]}</span>
          {trigger.confidence !== null
            ? ` · engine confidence ${(Number(trigger.confidence) * 100).toFixed(0)}%`
            : ""}
        </p>
      </header>

      {trigger.stale ? (
        <section className="rounded border border-indigo-900/60 bg-indigo-950/20 p-3">
          <h2 className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-indigo-400">
            <History className="size-3.5" /> Stale trigger
          </h2>
          <p className="mt-1 text-sm text-indigo-200">
            The MFN engine no longer computes this trigger from the fund&rsquo;s current facts — the
            source provision, commitment, or MFN clause changed since it last fired. It has{" "}
            <strong>not</strong> been auto-dismissed: only a human can close it out, via the status
            control below.
          </p>
          {trigger.stale_at ? (
            <p className="mt-1.5 text-[11px] text-indigo-500">
              Flagged stale {fmtDateTime(trigger.stale_at)}.
            </p>
          ) : null}
          {canReview && trigger.status !== "dismissed" ? (
            <Button
              size="sm"
              variant="secondary"
              className="mt-2.5"
              onClick={() => {
                setNextStatus("dismissed");
                setNotes(
                  (n) => n || "No longer computed by continuous MFN enforcement — facts changed.",
                );
              }}
            >
              Prefill dismiss
            </Button>
          ) : null}
        </section>
      ) : null}

      <section className="rounded border border-amber-900/50 bg-amber-950/20 p-3">
        <h2 className="text-[11px] uppercase tracking-wider text-amber-500">Engine rationale</h2>
        <p className="mt-1 text-sm text-amber-200">{trigger.rationale}</p>
        <p className="mt-2 text-[11px] text-amber-600">
          AI/engine output is a starting point — the decision below is the human record.
        </p>
      </section>

      {context ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <ProvisionExcerpt
            title={`MFN clause held by ${lpName(trigger.holder_lp_id)}`}
            provision={context.clauseProvision}
          />
          <ProvisionExcerpt
            title={`Term received by ${lpName(trigger.beneficiary_lp_id)}`}
            provision={context.triggering}
          />
        </div>
      ) : null}

      {trigger.resolution_notes ? (
        <section className="rounded border border-neutral-800 bg-neutral-900/60 p-3">
          <h2 className="text-[11px] uppercase tracking-wider text-neutral-500">
            Resolution notes
          </h2>
          <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-300">
            {trigger.resolution_notes}
          </p>
          {trigger.resolved_at ? (
            <p className="mt-1 text-[11px] text-neutral-600">
              Resolved {new Date(trigger.resolved_at).toLocaleString()}
            </p>
          ) : null}
        </section>
      ) : null}

      {canReview ? (
        <section className="max-w-xl space-y-3 rounded border border-neutral-800 bg-neutral-900/60 p-4">
          <h2 className="text-sm font-semibold text-neutral-100">Update status</h2>
          <div className="space-y-1">
            <label htmlFor="next-status" className="block text-xs font-medium text-neutral-300">
              New status
            </label>
            <select
              id="next-status"
              value={nextStatus}
              onChange={(e) => setNextStatus(e.target.value as MfnTriggerStatus)}
              className="block w-full rounded border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-neutral-200"
            >
              <option value="">Choose…</option>
              {NEXT_STATUSES.filter((s) => s !== trigger.status).map((s) => (
                <option key={s} value={s}>
                  {TRIGGER_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="notes" className="block text-xs font-medium text-neutral-300">
              Notes (kept in the audit record)
            </label>
            <textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Benefit extended via amendment dated …; or: clause excluded by §2(b) regulatory carve-out."
              className="block w-full rounded border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-neutral-200 placeholder:text-neutral-600"
            />
          </div>
          {transition.error ? (
            <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
              {transition.error.message}
            </div>
          ) : null}
          <button
            type="button"
            disabled={transition.isPending || !nextStatus}
            onClick={() => transition.mutate()}
            className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 hover:bg-white disabled:opacity-50"
          >
            {transition.isPending ? "Saving…" : "Save status"}
          </button>
        </section>
      ) : null}
    </>
  );
}

export function MfnDetailPage() {
  return (
    <TenantLayout>
      <MfnDetailInner />
    </TenantLayout>
  );
}
